import type { BackgroundFilter } from 'src/hooks/use-car-search/removeNonFilterPropsFromFilter';

import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import isUndefinedOrNull from '@ahd/shared/tools/is-undefined-or-null';
import { uniq } from 'es-toolkit';

import { type ConfigFilter } from '../../../../_configs/types';

import type { FilterObject } from '../types';

type FilterMap = Record<ConfigFilter | Exclude<BackgroundFilter, 'order' | 'sort'> | 'volltext', FilterObject>;

const filterMap: FilterMap = {
    aktion: { facetKey: null, filterKey: 'special', type: 'check', icon: 'mdi:sale' },
    aufbau: { facetKey: 'category', filterKey: 'category', type: 'multiselect', icon: 'mdi:car-convertible' },
    aus: { facetKey: 'accessoirs', filterKey: 'accessoirs', type: 'multiselect', icon: 'mdi:playlist-check' },
    ez: { facetKey: 'firstRegistrationRange', filterKey: 'firstRegistration', type: 'range', icon: 'mdi:calendar-today' },
    ezVon: { facetKey: 'firstRegistrationRange', filterKey: 'firstRegistrationFrom', type: 'singleselect', icon: 'mdi:calendar-today' },
    ezBis: { facetKey: 'firstRegistrationRange', filterKey: 'firstRegistrationTo', type: 'singleselect', icon: 'mdi:calendar-today' },
    farbe: { facetKey: 'exteriorColor', filterKey: 'exteriorColor', type: 'singleselect', icon: 'mdi:palette' },
    fd: { facetKey: 'fd', filterKey: 'fd', type: 'multiselect', icon: 'mdi:cash-100' },
    getriebe: { facetKey: 'gearbox', filterKey: 'gearbox', type: 'singleselect', icon: 'mdi:car-shift-pattern' },
    hasSeal: { facetKey: null, filterKey: 'hasSeal', type: 'check', icon: 'mdi:seal' },
    hersteller: { facetKey: 'make', filterKey: 'make', type: 'singleselect', icon: 'mdi:car' },
    locExclude: { facetKey: null, filterKey: 'locExclude', type: 'multiselect', icon: null },
    locInclude: { facetKey: null, filterKey: 'locInclude', type: 'multiselect', icon: null },
    locSourceExclude: { facetKey: null, filterKey: 'locSourceExclude', type: 'multiselect', icon: null },
    locSourceInclude: { facetKey: null, filterKey: 'locSourceInclude', type: 'multiselect', icon: null },
    ignoreStandort: { facetKey: null, filterKey: 'ignoreStandort', type: 'multiselect', icon: null },
    kategorie: { facetKey: 'vehicleClass', filterKey: 'vehicleClass', type: 'multiselect', icon: 'mdi:rv-truck' },
    km: { facetKey: 'mileage', filterKey: 'mileage', type: 'range', filterComponentProps: { einheit: 'km', step: 500 }, icon: 'mdi:counter' },
    kmVon: { facetKey: 'mileage', filterKey: 'mileageFrom', type: 'singleselect', icon: 'mdi:counter' },
    kmBis: { facetKey: 'mileage', filterKey: 'mileageTo', type: 'singleselect', icon: 'mdi:counter' },
    leistung: { facetKey: 'power', filterKey: 'power', type: 'range', filterComponentProps: { einheit: 'kW' }, icon: 'mdi:performance' },
    leistungVon: { facetKey: 'power', filterKey: 'powerFrom', type: 'singleselect', icon: 'mdi:performance' },
    leistungBis: { facetKey: 'power', filterKey: 'powerTo', type: 'singleselect', icon: 'mdi:performance' },
    modell: { facetKey: 'model', filterKey: 'model', type: 'singleselect', icon: 'mdi:tag' },
    motor: { facetKey: 'fuel', filterKey: 'fuel', type: 'multiselect', icon: 'mdi:engine' },
    nfzHersteller: { facetKey: null, filterKey: 'nfzHersteller', type: 'check', icon: null },
    nfz: { facetKey: null, filterKey: 'nfz', type: 'check', icon: null },
    ort: { facetKey: 'ort', filterKey: 'ort', type: 'multiselect', icon: 'mdi:map-marker' },
    moreCars: { facetKey: null, filterKey: 'moreCars', type: 'number', icon: null },
    pageSize: { facetKey: null, filterKey: 'pageSize', type: 'number', icon: null },
    preis: { facetKey: 'price', filterKey: 'price', type: 'range', filterComponentProps: { einheit: '€', step: 100 }, icon: 'mdi:currency-eur' },
    preisVon: { facetKey: 'price', filterKey: 'priceFrom', type: 'singleselect', icon: 'mdi:currency-eur' },
    preisBis: { facetKey: 'price', filterKey: 'priceTo', type: 'singleselect', icon: 'mdi:currency-eur' },
    rate: { facetKey: 'rate', filterKey: 'rate', type: 'range', filterComponentProps: { einheit: '€', step: 10 }, icon: 'material-symbols:30fps-select' },
    rateVon: { facetKey: 'price', filterKey: 'rateFrom', type: 'singleselect', icon: 'material-symbols:30fps-select' },
    rateBis: { facetKey: 'price', filterKey: 'rateTo', type: 'singleselect', icon: 'material-symbols:30fps-select' },
    seller: { facetKey: 'seller', filterKey: 'seller', type: 'multiselect', icon: 'mdi:map-marker' },
    sellerExclude: { facetKey: null, filterKey: 'sellerExclude', type: 'multiselect', icon: null },
    sellerInclude: { facetKey: null, filterKey: 'sellerInclude', type: 'multiselect', icon: null },
    siegel: { facetKey: 'usedCarSeal', filterKey: 'usedCarSeal', type: 'singleselect', icon: 'mdi:shield-car' },
    spacer: undefined,
    sofort: { facetKey: null, filterKey: 'available', type: 'check', icon: 'mdi:map-marker-check' },
    standort: { facetKey: 'location', filterKey: 'location', type: 'multiselect', icon: 'mdi:map-marker' },
    tags: { facetKey: 'tags', filterKey: 'tags', type: 'singleselect', icon: 'mdi:sale' },
    tagsExclude: { facetKey: null, filterKey: 'tagsExclude', type: 'singleselect', icon: null },
    typ: {
        facetKey: 'condition',
        filterKey: 'condition',
        type: 'multiselect',
        icon: 'mdi:train-car',
        filterComponentProps: {
            manipulateValueBeforeSend(origVal, newValueOrig, options) {
                let firedItem: { item: string | number; state: boolean };
                const newValue = !isUndefinedOrNull(newValueOrig?.props?.value) ? newValueOrig?.props?.value : undefined;
                const newValueOption = options?.find((o) => o?.i === newValue);
                const newValueState: (string | number)[] = Object.assign([], origVal);
                if (!isUndefinedNullOrEmpty(newValueOption)) {
                    if (newValueState.includes(newValueOption.i)) {
                        newValueState.splice(newValueState.indexOf(newValueOption.i), 1);
                        firedItem = { item: newValue, state: false };
                    } else {
                        newValueState.push(newValueOption.i);
                        firedItem = { item: newValue, state: true };
                    }
                }
                if (!isUndefinedNullOrEmpty(firedItem) && firedItem.item === 'USED' && firedItem.state === true) {
                    const additionalArr = options
                        .filter((e) => e?.i === 'EMPLOYEES_CAR' || e?.i === 'PRE_REGISTRATION' || e?.i === 'DEMONSTRATION')
                        .reduce((prev, curr) => [...prev, curr?.iSend || curr?.i], []);
                    return uniq([...newValueState, ...additionalArr]);
                }

                return newValueState;
            },
        },
    },
    volltext: { facetKey: null, filterKey: 'searchText', type: 'text', icon: 'mdi:magnify' },
};

export default filterMap;
